import { CheckOutlined, CloseOutlined } from "@ant-design/icons";
import { ComposerPrimitive, useComposerRuntime } from "@assistant-ui/react";
import type React from "react";
import { useEffect, useRef, useState, useSyncExternalStore } from "react";
import styles from "../Superdoc.module.scss";
import { activeDictation } from "./liveDictation";

const BAR_WIDTH = 3;
const BAR_GAP = 2;
const SAMPLE_MS = 60;

const formatElapsed = (ms: number) => {
  const seconds = Math.floor(ms / 1000);
  return `${Math.floor(seconds / 60)}:${String(seconds % 60).padStart(2, "0")}`;
};

/** Scrolling waveform of the live mic level, newest bar on the right. */
const Waveform: React.FC<{ getLevel: () => number }> = ({ getLevel }) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext("2d");
    if (!canvas || !ctx) return;
    const color = getComputedStyle(canvas).color;
    const levels: number[] = [];
    let smoothed = 0;
    let lastSample = 0;
    let frame = 0;

    const draw = (now: number) => {
      const dpr = window.devicePixelRatio || 1;
      const width = canvas.clientWidth;
      const height = canvas.clientHeight;
      if (canvas.width !== width * dpr) {
        canvas.width = width * dpr;
        canvas.height = height * dpr;
      }
      const maxBars = Math.floor(width / (BAR_WIDTH + BAR_GAP));
      smoothed = Math.max(getLevel(), smoothed * 0.8);
      if (now - lastSample >= SAMPLE_MS) {
        lastSample = now;
        levels.push(smoothed);
        if (levels.length > maxBars) levels.splice(0, levels.length - maxBars);
      }

      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      ctx.clearRect(0, 0, width, height);
      ctx.fillStyle = color;
      for (let i = 0; i < maxBars; i++) {
        const level = levels[levels.length - maxBars + i] ?? 0;
        const barHeight = Math.max(3, level * height);
        const x = i * (BAR_WIDTH + BAR_GAP);
        ctx.globalAlpha = level > 0.05 ? 1 : 0.35;
        ctx.fillRect(x, (height - barHeight) / 2, BAR_WIDTH, barHeight);
      }
      frame = requestAnimationFrame(draw);
    };
    frame = requestAnimationFrame(draw);
    return () => cancelAnimationFrame(frame);
  }, [getLevel]);

  return <canvas ref={canvasRef} className={styles["dictation__waveform"]} />;
};

const silence = () => 0;

/** Replaces the composer buttons while dictating: cancel, live waveform + timer, done. */
const DictationBar: React.FC = () => {
  const live = useSyncExternalStore(activeDictation.subscribe, activeDictation.get);
  const composer = useComposerRuntime();
  const [now, setNow] = useState(Date.now());

  useEffect(() => {
    const timer = setInterval(() => setNow(Date.now()), 250);
    return () => clearInterval(timer);
  }, []);

  const cancel = () => {
    live?.discard();
    composer.stopDictation();
  };

  return (
    <div className={styles.dictation}>
      <button type="button" className={styles["dictation__cancel"]} onClick={cancel} title="Discard">
        <CloseOutlined />
      </button>
      <span className={styles["dictation__dot"]} />
      <Waveform getLevel={live?.getLevel ?? silence} />
      <span className={styles["dictation__timer"]}>{formatElapsed(live ? now - live.startedAt : 0)}</span>
      <ComposerPrimitive.StopDictation className={styles["dictation__done"]} title="Done">
        <CheckOutlined />
      </ComposerPrimitive.StopDictation>
    </div>
  );
};

export default DictationBar;
